/**
 * Org Settings → General. Rename + read-only slug.
 *
 * Owner+admin can edit; member/reviewer see the form disabled.
 */
import { useState, type FormEvent } from 'react'
import { Button, Input, Label } from '@/components/ui'
import type { OrgWithRole } from '@/lib/api'
import { orgsAPI } from '@/lib/api'
import { toUserMessage } from '@/lib/userMessage'
import { useCanI } from '@/hooks/useCanI'

export function OrgGeneral({ org, onUpdated }: { org: OrgWithRole; onUpdated: () => Promise<void> }) {
  const canEdit = useCanI('org.update', org._id)
  const [name, setName] = useState(org.name)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Name is required.')
      return
    }
    if (trimmed === org.name) {
      // Nothing to save — surface a quick visual confirmation but
      // skip the round-trip.
      setSavedAt(Date.now())
      return
    }
    setSubmitting(true)
    try {
      await orgsAPI.update(org._id, { name: trimmed })
      await onUpdated()
      setSavedAt(Date.now())
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't save the organization settings. Try again — if the problem continues, check your connection.",
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
      <div className="space-y-2">
        <Label htmlFor="org-name">Organization name</Label>
        <Input
          id="org-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canEdit || submitting}
          required
        />
        {!canEdit && (
          <p className="text-xs text-muted-foreground">
            Only owners and admins can rename the organization.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="org-slug">URL slug</Label>
        <Input
          id="org-slug"
          type="text"
          value={org.slug}
          disabled
          readOnly
        />
        <p className="text-xs text-muted-foreground">
          Slug stays stable across renames so existing links don't break.
          Changing it lands as a separate affordance later.
        </p>
      </div>

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {savedAt && !error && (
        <p className="text-sm text-success" key={savedAt}>Saved.</p>
      )}

      {canEdit && (
        <div className="flex gap-2">
          <Button type="submit" disabled={submitting || name.trim() === org.name}>
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={submitting || name === org.name}
            onClick={() => setName(org.name)}
          >
            Reset
          </Button>
        </div>
      )}
    </form>
  )
}
